import * as React from 'react';
import pwWebSvg from '../../../assets/pwWebSvg';
import { Project, PwWebConection, SelectItem } from '../../../types';
import { ConnectionSelection } from './ConnectionSelection';
import { parseEntityId } from '../../../utils/parsers';
import { LoadingPage } from '../../Shared';
import { AuthService } from '../../../services';
import { app, pages } from "@microsoft/teams-js";
import { usePromise } from '../../../hooks';

export interface ConnectionSelectionContainerProps {
  project: Project;
  tabConfigured: boolean;
  connections?: PwWebConection[];
  connection?: PwWebConection;
  tabIsDefault: boolean;
  loadConnections: () => Promise<any>;
  loadServices: () => Promise<void>;
  setConnection: (connection: PwWebConection) => any;
  setTabName: (tabId: string) => any;
  loadTabIsDefault: () => Promise<any>;
  setTabIsDefault: (newValue: boolean) => any;
}

export function ConnectionSelectionContainer(
  props: ConnectionSelectionContainerProps
) {
  const user = usePromise(AuthService.getUser());
  const [loading, setLoading] = React.useState(true);
  const [mappedConnections, setMappedConnections] = React.useState(
    [] as SelectItem[]
  );

  React.useEffect(() => {
    (async () => {
      await props.loadConnections();
      await props.loadServices();
    })().catch(error => console.error(error));
  }, []);

  React.useEffect(() => {
    (async () => {
      if (!props.connections) return;

      props.setTabName('');

      await props.loadTabIsDefault();
      const existingTabs = await getExistingTabs();

      const connectionsList = props.connections
        ? props.connections.map(connection => {
            const duplicateTab = existingTabs.find(
              tab => parseEntityId(tab.entityId).connectionId === connection.id
            )?.tabName;
            const disabled = duplicateTab && duplicateTab.length > 0;

            return {
              text: `${connection.name}${disabled ? ' (in use)' : ''}`,
              value: connection.id,
              onClick: disabled
                ? undefined
                : async () => {
                    props.setConnection(connection);
                    await props.loadTabIsDefault();
                  },
              icon: pwWebSvg,
              title: disabled
                ? `This connection is in use on this channel (${duplicateTab})`
                : '',
              disabled,
            } as SelectItem;
          })
        : [];

      setLoading(false);
      setMappedConnections(connectionsList);
    })().catch(error => console.error(error));
  }, [props.connections]);

  const getExistingTabs = async (): Promise<microsoftTeams.TabInstance[]> => {
    const context = await app.getContext();
    const existingTabs = await pages.tabs.getTabInstances();

    const tabsOnChannel = existingTabs.teamTabs.filter(
      tab =>
        tab.entityId !== context.entityId &&
        tab.teamId === context.teamId &&
        tab.channelId === context.channelId &&
        parseEntityId(tab.entityId).projectId === props.project.id
    );
    return tabsOnChannel;
  };

  const displayMainButtonText = () => {
    return props.connection ? props.connection.name : 'Select Connection';
  };

  const getLabel = () =>
    displayMainButtonText() !== 'Select Connection'
      ? 'Selected Connection'
      : 'Select Connection';

  return loading ? (
    <LoadingPage subtext='Finding your connections...' />
  ) : (
    <ConnectionSelection
      user={user}
      tabConfigured={props.tabConfigured}
      mappedConnections={mappedConnections}
      connection={props.connection}
      label={getLabel()}
      setTabIsDefault={props.setTabIsDefault}
      tabIsDefault={props.tabIsDefault}
    />
  );
}
